import { useFeatureAccess } from "@src/auth/permissions/FeatureAccessContext";
import {
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser } from "../../context/UserContext";
import { DesignDoc, DocumentGroup } from "../../types";
import {
  getDesignDocGroups,
  getDesignDocs,
  patchDesignDocGroups,
  postDesignDocGroups,
} from "./designDocsApi";

export const DESIGN_DOC_GROUPS_QUERY_KEY = "designDocGroups";
export const useQueryDesignDocGroups = (siteId: number) => {
  const enabled = !!useFeatureAccess("Documents");

  return useQueryWithAccessCheckLog(
    ["Documents", "read"],
    (checkFeatureAccessHeader) =>
      getDesignDocGroups(siteId, checkFeatureAccessHeader),
    {
      enabled,
      staleTime: 1000 * 60,
      queryKey: [DESIGN_DOC_GROUPS_QUERY_KEY, siteId],
    }
  );
};

export function useQueryDesignDocs<TSelect = DesignDoc[]>(
  siteId: number,
  select?: (data?: DesignDoc[]) => TSelect
) {
  const enabled = !!useFeatureAccess("Documents");

  return useQueryWithAccessCheckLog(
    ["Documents", "read"],
    (checkFeatureAccessHeader) =>
      getDesignDocs(siteId, checkFeatureAccessHeader),
    { select, enabled, staleTime: 1000 * 60, queryKey: ["design-docs", siteId] }
  );
}
export const usePostDesignDocGroup = (siteId: number) => {
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  return useMutationWithAccessCheckLog(
    ["Documents", "edit"],
    (
      group: Pick<DocumentGroup, "groupname" | "parentGroup">,
      checkFeatureAccessHeader
    ) =>
      postDesignDocGroups(
        {
          ...group,
          siteId,
          createdBy: currentUser.id,
        },
        checkFeatureAccessHeader
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [DESIGN_DOC_GROUPS_QUERY_KEY, siteId],
        });
      },
    }
  );
};

export const usePatchDesignDocGroup = (siteId: number) => {
  const queryClient = useQueryClient();

  return useMutationWithAccessCheckLog(
    ["Documents", "edit"],
    (
      group: Pick<DocumentGroup, "id" | "groupname">,
      checkFeatureAccessHeader
    ) => patchDesignDocGroups(group, checkFeatureAccessHeader),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [DESIGN_DOC_GROUPS_QUERY_KEY, siteId],
        });
      },
    }
  );
};
